import { Injector } from '@angular/core';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  ButtonActionType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { take } from 'rxjs';
import { AccountService } from '../../../services/account-service/account-service.service';
import { TransactionState } from '../../../state/transaction.state';
import { showAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { handleRedirection } from '../cta-redirection.utils';

type Deps = {
  store: Store;
  alertHandler: AlertHandlerService;
  accountService: AccountService;
};

const getDeps = (injector: Injector): Deps => ({
  store: injector.get(Store),
  alertHandler: injector.get(AlertHandlerService),
  accountService: injector.get(AccountService),
});

/* 
  This function is used to cancel a scheduled payment. It gets the current transaction id from the TransactionState and calls the removeScheduledPayment API. Based on the response it redirects the user to the success or failure page. If there is no transaction id it shows an alert.
 */
export const cancelScheduledPayment = (widgetProperties: BaseWidgetProperties, injector: Injector) => {
  const { store, alertHandler, accountService } = getDeps(injector);

  const transactionState = store.selectSnapshot(TransactionState.getTransactions);
  const transactionId = transactionState?.currentTransactionId;

  if (!transactionId) {
    showAlertFromCode(alertHandler, {
      title: 'Error',
      type: 'error',
      name: 'Transaction ID Missing',
      description: 'Transaction ID is required to cancel scheduled payment',
    });
    return;
  }

  accountService
    .removeScheduledPayment(transactionId)
    .pipe(take(1))
    .subscribe({
      next: () => {
        handleRedirection(
          widgetProperties,
          injector,
          ActionButtonOutcomesType.SUCCESS,
          true,
          false,
          ButtonActionType.CANCEL_SCHEDULED_PAYMENT,
        );
      },
      error: () => {
        handleRedirection(
          widgetProperties,
          injector,
          ActionButtonOutcomesType.FAILURE,
          true,
          false,
          ButtonActionType.CANCEL_SCHEDULED_PAYMENT,
        );
        Sentry.addBreadcrumb({
          category: 'Cancel Scheduled Payment',
          message: 'Failed to cancel scheduled payment',
          level: 'error',
        });
      },
    });
};
